import {v4 as uuid} from 'uuid';

import * as validation from '../../lib/validation';
import {buildCatalogTree, buildNameListFromTreeForAnswers} from '../../lib/catalog';

// Mirrors the backend MeasurementUnit enum. Keys are the enum names used as values stored
// in questionMeta; labels are the display strings shown beside question titles.
export const MEASUREMENT_UNITS = [
  {value: 'MG', label: 'mg', category: 'WEIGHT'},
  {value: 'G',  label: 'g',  category: 'WEIGHT'},
  {value: 'KG', label: 'kg', category: 'WEIGHT'},
  {value: 'OZ', label: 'oz', category: 'WEIGHT'},
  {value: 'LB', label: 'lb', category: 'WEIGHT'},
  {value: 'ML', label: 'ml', category: 'VOLUME'},
  {value: 'L',  label: 'L',  category: 'VOLUME'}
];

const MEASUREMENT_UNIT_LABELS = MEASUREMENT_UNITS.reduce((map, unit) => {
  map[unit.value] = unit.label;
  return map;
}, {});

export function getMeasurementUnit(question) {
  if (!question || question.type !== 'MEASURED_NUMERIC') return null;
  const value = (question.questionMeta || [])
    .filter(meta => meta.key === 'measurementUnit')
    .map(meta => meta.value)[0];
  return value || null;
}

export function getMeasurementUnitLabel(question) {
  const value = getMeasurementUnit(question);
  return value ? MEASUREMENT_UNIT_LABELS[value] || null : null;
}

export function getScaleTarget(question) {
  if (!question || question.type !== 'MEASURED_NUMERIC') return false;
  return (question.questionMeta || []).some(meta => meta.key === 'scaleTarget' && meta.value === 'true');
}

export function getQuestionDisplayLabel(question) {
  const unit = getMeasurementUnitLabel(question);
  return unit ? `${question.text} (${unit})` : question.text;
}

export function createQuestionOption(value, order) {
  return {value, order};
}

export function getLogicBuilderVariables(form, catalog, question = null, options = {}) {
  const nameOptions = buildNameListFromTreeForAnswers('', '', buildCatalogTree(catalog))
    .map(item => ({name: item.name, value: item.id}));

  form.questions.sort(function(a,b) {return a.order - b.order});

  return [...form.contextQuestions, ...form.questions]
    .filter(q => question ? q.id !== question.id : true)
    .filter(q => !isQuestionDeleted(q))
    .filter(q => q.type !== 'IMAGE' && q.type !== 'SIGNATURE')
    .filter(q => options.excludeNumericGrids ? q.type !== 'NUMERIC_GRID' : true)
    .filter(q => options.excludeMultiValue ? (q.type !== 'NUMERIC_GRID' && q.type !== 'MULTI_NUMERIC') : true)
    .reduce((map, question) => {
      let questionOptions = question.questionOptions;

      if (question.type === 'CATALOG_RADIO' || question.type === 'CATALOG_CHECKBOX') {
        questionOptions = nameOptions;
      }

      map[question.id] = {
        label: question.text,
        type: question.type,
        isMultiValue: question.type === 'MULTI_NUMERIC' || question.type === 'NUMERIC_GRID',
        options: questionOptions.map(option => ({
          name: option.name ? option.name : option.value,
          value: option.value
        }))
      };

      return map;
    }, {});
}

const isValuePresent = validation.create((value, {isEmpty, trim}) => !isEmpty(trim(value)));

function isValidJsonLogic(expression) {
  try {
    return JSON.parse(expression) !== null;
  }
  catch (e) {
    return false;
  }
}

export function getValidationErrors(form, options = {disabled: false}) {
  return {
    name: isValuePresent(form.name) || options.disabled ? null : 'A form name is required',
    questions: form.questions.some(question => question.required && !isQuestionDeleted(question)) || options.disabled ? null : 'At least one repeated question must be required',
    questionsById: [...form.contextQuestions, ...form.questions].filter(question => !isQuestionDeleted(question)).reduce((map, question) => {
      map[question.id] = {
        text: isValuePresent(question.text) || options.disabled ? null : 'A question title is required',
        type: isValuePresent(question.type) || options.disabled ? null : 'A type is required',
        defaultAnswers: null,
        codeGenerator: question.type === 'CODE_GENERATOR' && !options.disabled ? getCodeGeneratorValidation(question) : null,
        questionOptionsByValue: hasChoices(question.type) ? question.questionOptions.reduce((map, option) => {
          map[option.value] = null;
          
          if (!options.disabled) {
            if (!isValuePresent(option.value)) {
              map[option.value] = 'An option value is required';
            }
            else if (question.questionOptions.filter(o => o.value === option.value).length > 1) {
              map[option.value] = 'Duplicate option';
            }
          }
          
          return map;
        }, {}) : {}
      };

      return map;
    }, {}),
    alertsById: form.alerts.reduce((map, alert) => {
      map[alert.id] = {
        name: isValuePresent(alert.name) || options.disabled ? null : 'An alert name is required',
        expression: isValidJsonLogic(alert.expression) || options.disabled ? null : 'A condition to trigger this alert is required'
      };

      return map;
    }, {}),
    contextAlertsById: form.contextAlerts.reduce((map, contextAlert) => {
      map[contextAlert.id] = {
        name: isValuePresent(contextAlert.name) || options.disabled ? null : 'A contextAlert name is required',
        formId: isValuePresent(contextAlert.formId) || options.disabled ? null : 'A form to complete is required',
        description: isValuePresent(contextAlert.description) || options.disabled ? null : 'A contextAlert description is required',
        expression: isValidJsonLogic(contextAlert.expression) || options.disabled ? null : 'A condition to trigger this contextAlert is required'
      };

      return map;
    }, {}),
    // get all notificationExpressions
    notificationExpressionErrors: (() => {
      const groups = [
        form.rowCompleteContacts || [],
        form.rowApprovedContacts || [],
        form.rowRejectedContacts || []
      ];
      // flatten to each expression
      const allUserExprs = groups.flat();
      // ensure that userId is not missing
      const nullUserId = allUserExprs.some(expr => {
        return !(expr.userId || (expr.user && expr.user.id));
      });
      // if null User stop user from saving form
      return nullUserId
          ? ["A user must be selected for all notification recipients"]
          : [];
    })(),
    reviewers: form.reviewers.length === 0 && (form.rowApprovedContacts.length > 0 || form.rowRejectedContacts.length > 0)
      ? 'At least one reviewer is required' : null
  }; 
}

export function hasValidationErrors(validations) {
  if (validations === null) {
    return false;
  }

  if (Array.isArray(validations)) {
    return validations.some(validation => hasValidationErrors(validation));
  }

  if (typeof validations === 'object') {
    return Object.keys(validations)
      .map(key => validations[key])
      .some(validation => hasValidationErrors(validation));
  }

  return true;
}

export function flattenValidationErrors(validations) {
  if (validations === null) {
    return [];
  }

  if (Array.isArray(validations)) {
    return validations.reduce((acc, validation) => acc.concat(flattenValidationErrors(validation)), []);
  }

  if (typeof validations === 'object') {
    return Object.keys(validations)
      .map(key => validations[key])
      .reduce((acc, validation) => acc.concat(flattenValidationErrors(validation)), []);
  }

  return [validations];
}

export function hasChoices(questionType) {
  return questionType === 'RADIO' || questionType === 'CHECKBOXES';
}


export const CODE_TYPES = [
  {value: 'JULIAN_DATE', label: 'Julian Date'},
  {value: 'DATE_CODE', label: 'Date Code'}
];

// allowed format tokens per code type- this must match what the api validates
export const JULIAN_FORMATS = [
  {value: 'YYDDD', label: 'YYDDD'},
  {value: 'DDDYY', label: 'DDDYY'},
  {value: 'DDDYYYY', label: 'DDDYYYY'},
  {value: 'DDDY', label: 'DDDY'}
];

export const DATE_CODE_FORMATS = [
  {value: 'YYMMDD', label: 'YYMMDD'},
  {value: 'MMDDYY', label: 'MMDDYY'},
  {value: 'DDMMYY', label: 'DDMMYY'},
  {value: 'YYYYMMDD', label: 'YYYYMMDD'},
  {value: 'MMDDYYYY', label: 'MMDDYYYY'},
  {value: 'DDMMYYYY', label: 'DDMMYYYY'}
];

const ALLOWED_CODE_TYPES = CODE_TYPES.map(type => type.value);

//the max length of a prefix/suffix
export const MAX_CODE_AFFIX_LENGTH = 20;

// fixed sample date used for builder previews: April 28, 2026 -> YYDDD = "26118"
// real generation happens on the mobile device using the phone's local date,
// so previews are illustrative only.
export const CODE_PREVIEW_SAMPLE_DATE = new Date(2026, 3, 28);

export function getCodeGeneratorConfig(question) {
  const read = key => getQuestionMetadata(question, key)[0] || '';
  return {
    codeType: read('codeType'),
    codePrefix: read('codePrefix'),
    codeSuffix: read('codeSuffix'),
    codeFormat: read('codeFormat')
  };
}

function dayOfYear(date) {
  // compute in UTC so daylight-saving transitions between Jan 1 and the date does not
  // shift the result by a day.
  const startOfYear = Date.UTC(date.getFullYear(), 0, 0);
  const current = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.floor((current - startOfYear) / 86400000);
}

// approximates the mobile app's on-device date formatting for a single date so the
// builder can show a preview. greedy longest-token match keeps DDD (day-of-year) and
// YYYY from being shadowed by DD / YY.
function applyDateFormat(format, date) {
  const yyyy = String(date.getFullYear());
  const tokens = [
    ['YYYY', yyyy],
    ['DDD', String(dayOfYear(date)).padStart(3, '0')],
    ['MM', String(date.getMonth() + 1).padStart(2, '0')],
    ['DD', String(date.getDate()).padStart(2, '0')],
    ['YY', yyyy.slice(-2)],
    ['Y', yyyy.slice(-1)]
  ];

  let result = '';
  let i = 0;
  while (i < format.length) {
    let matched = false;
    for (let t = 0; t < tokens.length; t++) {
      const [token, value] = tokens[t];
      if (format.startsWith(token, i)) {
        result += value;
        i += token.length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      result += format[i];
      i += 1;
    }
  }
  return result;
}

// a best effort preview of the code a question would generate. Returns '' when the
// config is too incomplete to preview.
export function createCodePreview(config, sampleDate = CODE_PREVIEW_SAMPLE_DATE) {
  if (!config || !config.codeType) return '';
  if (config.codeType !== 'JULIAN_DATE' && config.codeType !== 'DATE_CODE') return '';
  if (!config.codeFormat) return '';

  const prefix = config.codePrefix || '';
  const suffix = config.codeSuffix || '';
  const core = applyDateFormat(config.codeFormat, sampleDate);

  return `${prefix}${core}${suffix}`;
}

// validates the CODE_GENERATOR config, mirroring the rules the api enforces on a save
export function getCodeGeneratorValidation(question) {
  const config = getCodeGeneratorConfig(question);
  const errors = {codeType: null, codeFormat: null, codePrefix: null, codeSuffix: null};

  // the prefix and suffix are both optional, but when set each is capped independently
  if (config.codePrefix.length > MAX_CODE_AFFIX_LENGTH) {
    errors.codePrefix = `Prefix can be at most ${MAX_CODE_AFFIX_LENGTH} characters`;
  }
  if (config.codeSuffix.length > MAX_CODE_AFFIX_LENGTH) {
    errors.codeSuffix = `Suffix can be at most ${MAX_CODE_AFFIX_LENGTH} characters`;
  }

  if (!isValuePresent(config.codeType)) {
    errors.codeType = 'A code type is required';
    return errors;
  }

  if (!ALLOWED_CODE_TYPES.includes(config.codeType)) {
    errors.codeType = 'Invalid code type';
    return errors;
  }

  const allowed = config.codeType === 'JULIAN_DATE' ? JULIAN_FORMATS : DATE_CODE_FORMATS;
  if (!isValuePresent(config.codeFormat)) {
    errors.codeFormat = 'A format is required';
  }
  else if (!allowed.some(format => format.value === config.codeFormat)) {
    errors.codeFormat = 'Invalid format for this code type';
  }

  return errors;
}

export function createQuestion(order) {
  return {
    id: uuid(),
    order,
    context: false,
    text: '',
    type: '',
    defaultAnswers: [],
    required: true,
    questionMeta: [],
    questionOptions: [],
    condition: null
  };
}

export function getQuestionMetadata(question, key) {
  return question.questionMeta
    .filter(metadata => metadata.key === key)
    .map(metadata => metadata.value);
}

// Usage in other files to check if question was soft deleted from a published form
export function isQuestionDeleted(question){
  return question.deleted;
}